import {
  publicGiftCertificateSchema,
  publicTreatmentPlanErrorSchema,
  publicTreatmentPlanEventResponseSchema,
  publicTreatmentPlanResponseSchema,
  staffCommunicationsResponseSchema,
  staffDashboardResponseSchema,
  staffGiftCertificateMutationResponseSchema,
  staffGiftCertificatesResponseSchema,
  staffPatientCommentsResponseSchema,
  staffPatientActivityResponseSchema,
  staffPatientMedicalCardsResponseSchema,
  staffPatientSegmentsResponseSchema,
  staffPatientVerificationCodesResponseSchema,
  staffPatientWorkspaceResponseSchema,
  staffPatientDiagnocatResponseSchema,
  staffPatientSegmentsInsightResponseSchema,
  staffPatientContextInsightResponseSchema,
  staffPatientDiagnocatPdfResponseSchema,
  staffPatientsListResponseSchema,
  staffOperationsResponseSchema,
  staffPlansResponseSchema,
  staffScheduleResponseSchema,
  staffTreatmentPlanResponseSchema,
  staffUsersResponseSchema,
  staffUsersCatalogResponseSchema,
  staffUsersMutationResponseSchema,
  staffPromotionsListResponseSchema,
  staffSegmentsResponseSchema,
  staffPromotionMutationResponseSchema,
  type PublicTreatmentPlan,
  type StaffPromotionsListResponse,
  type StaffSegmentsResponse,
  type StaffPromotionInput,
} from '@unified-lk/api-contracts'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type {
  AuthSession,
  MisShareData,
  MisShareInput,
  PublicPlanFailure,
  PublicGiftCertificateData,
  PublicGiftCertificateFailure,
  ShareSettingsInput,
  StaffCommunicationsData,
  StaffDashboardData,
  StaffGiftCertificate,
  StaffGiftCertificateFormInput,
  StaffGiftCertificatesData,
  StaffPatientCommentsData,
  StaffPatientActivityData,
  StaffPatientMedicalCardsData,
  StaffPatientSegmentsData,
  StaffPatientVerificationCodesData,
  StaffPatientWorkspaceData,
  StaffPatientsListData,
  StaffPlanSummary,
  StaffScheduleData,
  StaffPatientSegmentsInsight,
  StaffPatientContextInsight,
  StaffOperationsData,
  StaffUsersData,
} from '../types/plans'

async function parseJson(response: Response) {
  return response.json() as Promise<unknown>
}

async function authorizedRequest(path: string, session: AuthSession, init?: RequestInit) {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
      ...(init?.headers ?? {}),
    },
  })

  const json = await parseJson(response)

  if (!response.ok) {
    const message = typeof json === 'object' && json !== null
      ? String((json as Record<string, unknown>).message ?? (json as Record<string, unknown>).error ?? 'API request failed')
      : 'API request failed'

    throw new Error(message)
  }

  return json
}

async function authorizedBlob(path: string, session: AuthSession, init?: RequestInit) {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: 'text/csv,application/octet-stream;q=0.9,*/*;q=0.8',
      Authorization: `Bearer ${session.accessToken}`,
      ...(init?.headers ?? {}),
    },
  })

  if (!response.ok) {
    throw new Error('API request failed')
  }

  return response.blob()
}

function publicPlanUrl(bootstrap: TenantBootstrapModel, token: string) {
  return `${bootstrap.apiBase}/public/plans/${encodeURIComponent(token)}`
}

function publicPlanEventUrl(bootstrap: TenantBootstrapModel, token: string) {
  return `${publicPlanUrl(bootstrap, token)}/events`
}

function publicConstructorPlanUrl(bootstrap: TenantBootstrapModel, token: string) {
  return `${bootstrap.apiBase}/public/constructor-plans/${encodeURIComponent(token)}`
}

function publicConstructorPlanEventUrl(bootstrap: TenantBootstrapModel, token: string) {
  return `${publicConstructorPlanUrl(bootstrap, token)}/events`
}

export const staffApi = {
  async getPlans(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    opts?: { page?: number; perPage?: number; patientId?: number | null; status?: string | null; search?: string | null; sharing?: string | null },
  ): Promise<{ plans: StaffPlanSummary[]; total: number }> {
    const q = new URLSearchParams()
    if (opts?.page) q.set('page', String(opts.page))
    if (opts?.perPage) q.set('per_page', String(opts.perPage))
    if (opts?.patientId) q.set('patient_id', String(opts.patientId))
    if (opts?.status) q.set('status', opts.status)
    if (opts?.search) q.set('search', opts.search)
    if (opts?.sharing && opts.sharing !== 'all') q.set('sharing', opts.sharing)
    const url = `${bootstrap.apiBase}/staff/plans${q.toString() ? `?${q}` : ''}`
    const payload = await authorizedRequest(url, session)
    const parsed = staffPlansResponseSchema.parse(payload)

    return {
      plans: parsed.plans,
      total: parsed.total ?? parsed.plans.length,
    }
  },

  async getDashboard(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffDashboardData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/dashboard`, session)
    const parsed = staffDashboardResponseSchema.parse(payload)

    return {
      staff: parsed.staff,
      summary: parsed.summary,
      focus_blocks: parsed.focus_blocks,
      sources: parsed.sources,
      today_appointments: parsed.today_appointments,
    }
  },

  async getSchedule(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffScheduleData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/schedule`, session)
    const parsed = staffScheduleResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      days: parsed.days,
      appointments: parsed.appointments,
    }
  },

  async getCommunications(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffCommunicationsData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/communications`, session)
    const parsed = staffCommunicationsResponseSchema.parse(payload)

    return {
      summary: parsed.summary,
      channel_overview: parsed.channel_overview,
      threads: parsed.threads,
    }
  },

  async getPatientsList(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    options: { page?: number; perPage?: number; search?: string } = {},
  ): Promise<StaffPatientsListData> {
    const query = new URLSearchParams()
    if (options.page) query.set('page', String(options.page))
    if (options.perPage) query.set('per_page', String(options.perPage))
    if (options.search) query.set('search', options.search)

    const suffix = query.toString() ? `?${query.toString()}` : ''
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/list${suffix}`, session)
    const parsed = staffPatientsListResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      summary: parsed.summary,
      filters: parsed.filters,
      total: parsed.total,
      pages: parsed.pages,
      patients: parsed.patients,
    }
  },

  async getPatientMedicalCards(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    options: {
      fromDate?: string
      toDate?: string
      doctorId?: number | null
      patientId?: number | null
      recordId?: number | null
      limit?: number
    } = {},
  ): Promise<StaffPatientMedicalCardsData> {
    const query = new URLSearchParams()
    if (options.fromDate) query.set('from_date', options.fromDate)
    if (options.toDate) query.set('to_date', options.toDate)
    if (options.doctorId) query.set('doctor_id', String(options.doctorId))
    if (options.patientId) query.set('patient_id', String(options.patientId))
    if (options.recordId) query.set('record_id', String(options.recordId))
    if (options.limit) query.set('limit', String(options.limit))

    const suffix = query.toString() ? `?${query.toString()}` : ''
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/medical-cards${suffix}`, session)
    const parsed = staffPatientMedicalCardsResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      filters: parsed.filters,
      summary: parsed.summary,
      doctors: parsed.doctors,
      records: parsed.records,
      selected_patient: parsed.selected_patient ?? null,
      selected_record: parsed.selected_record ?? null,
    }
  },

  async downloadPatientMedicalCardsCsv(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    options: {
      fromDate?: string
      toDate?: string
      doctorId?: number | null
      patientId?: number | null
      recordId?: number | null
      limit?: number
    } = {},
  ): Promise<Blob> {
    const query = new URLSearchParams()
    if (options.fromDate) query.set('from_date', options.fromDate)
    if (options.toDate) query.set('to_date', options.toDate)
    if (options.doctorId) query.set('doctor_id', String(options.doctorId))
    if (options.patientId) query.set('patient_id', String(options.patientId))
    if (options.recordId) query.set('record_id', String(options.recordId))
    if (options.limit) query.set('limit', String(options.limit))

    const suffix = query.toString() ? `?${query.toString()}` : ''

    return authorizedBlob(`${bootstrap.apiBase}/staff/patients/medical-cards/export${suffix}`, session)
  },

  async getPatientComments(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffPatientCommentsData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/comments`, session)
    const parsed = staffPatientCommentsResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      summary: parsed.summary,
      comments: parsed.comments,
    }
  },

  async getPatientSegments(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffPatientSegmentsData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/segments`, session)
    const parsed = staffPatientSegmentsResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      summary: parsed.summary,
      segments: parsed.segments,
    }
  },

  async getPatientVerificationCodes(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    phone?: string,
  ): Promise<StaffPatientVerificationCodesData> {
    const query = new URLSearchParams()
    if (phone) query.set('phone', phone)

    const suffix = query.toString() ? `?${query.toString()}` : ''
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/verification-codes${suffix}`, session)
    const parsed = staffPatientVerificationCodesResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      summary: parsed.summary,
      codes: parsed.codes,
    }
  },

  async getPatientActivity(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    options: {
      days?: number
      search?: string
      eventType?: string
      limit?: number
      excludeVerificationScript?: boolean
    } = {},
  ): Promise<StaffPatientActivityData> {
    const query = new URLSearchParams()
    if (options.days) query.set('days', String(options.days))
    if (options.search) query.set('search', options.search)
    if (options.eventType) query.set('event_type', options.eventType)
    if (options.limit) query.set('limit', String(options.limit))
    if (options.excludeVerificationScript) query.set('exclude_verification_script', '1')

    const suffix = query.toString() ? `?${query.toString()}` : ''
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/activity${suffix}`, session)
    const parsed = staffPatientActivityResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      filters: parsed.filters,
      summary: parsed.summary,
      errors: parsed.errors ?? [],
      events: parsed.events,
    }
  },


  async getPatientSegmentsInsight(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    patientId: number,
  ): Promise<StaffPatientSegmentsInsight> {
    const payload = await authorizedRequest(
      `${bootstrap.apiBase}/staff/patients/${patientId}/segments-insight`,
      session,
    )
    const parsed = staffPatientSegmentsInsightResponseSchema.parse(payload)

    return {
      status: parsed.status,
      summary: parsed.summary,
      segments_guidance: parsed.segments_guidance,
      segments_count: parsed.segments_count,
      warning: parsed.warning,
      segments: parsed.segments ?? [],
    }
  },

  async getPatientContextInsight(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    patientId: number,
  ): Promise<StaffPatientContextInsight> {
    const payload = await authorizedRequest(
      `${bootstrap.apiBase}/staff/patients/${patientId}/context-summary`,
      session,
    )
    const parsed = staffPatientContextInsightResponseSchema.parse(payload)

    return {
      status: parsed.status,
      summary: parsed.summary,
      summary_preview: parsed.summary_preview,
      meta_summary_text: parsed.meta_summary_text,
    }
  },

  async getPatientDiagnocat(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    patientId: number,
  ) {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/${patientId}/diagnocat`, session)
    let parsed: ReturnType<typeof staffPatientDiagnocatResponseSchema.safeParse>
    try {
      parsed = staffPatientDiagnocatResponseSchema.safeParse(payload)
    } catch {
      parsed = { success: false as const, error: {} as never }
    }
    if (parsed.success) {
      return {
        sources: parsed.data.sources,
        linked: parsed.data.linked,
        status: parsed.data.status,
        legacy_patient_id: parsed.data.legacy_patient_id ?? patientId,
        total_studies: parsed.data.total_studies,
        total_analyses: parsed.data.total_analyses,
        ambiguous_matches: parsed.data.ambiguous_matches ?? [],
        studies: parsed.data.studies,
      }
    }
    const raw = payload as Record<string, unknown>
    const studies = Array.isArray(raw.studies) ? raw.studies : []
    return {
      sources: (raw.sources as Record<string, string>) ?? { diagnocat: "diagnostics_api" },
      linked: Boolean(raw.linked),
      status: String(raw.status ?? "unknown"),
      legacy_patient_id: Number(raw.legacy_patient_id ?? patientId),
      total_studies: Number(raw.total_studies ?? studies.length),
      total_analyses: Number(raw.total_analyses ?? 0),
      ambiguous_matches: Array.isArray(raw.ambiguous_matches) ? raw.ambiguous_matches : [],
      studies: studies as never[],
    }
  },



  async getVatechCtCatalog(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    params: { lkPatientId: number; misPatientId?: string; backfillDbPatientId?: boolean },
  ) {
    const q = new URLSearchParams({ lk_patient_id: String(params.lkPatientId) })
    if (params.misPatientId) q.set('mis_patient_id', params.misPatientId)
    if (params.backfillDbPatientId === false) q.set('backfill_db_patient_id', 'false')
    const payload = await authorizedRequest(
      `${bootstrap.apiBase}/staff/diagnocat/vatech-catalog?${q.toString()}`,
      session,
    )
    return payload as {
      success: boolean
      mis_patient_id?: string | null
      lk_patient_id?: number | null
      local_patient_id: number | null
      fullname: string | null
      patient_linked: boolean
      resolve_strategy: string | null
      studies: Array<{
        study_uid: string
        study_date: string | null
        folder_name?: string | null
        patient_name?: string | null
        modality?: string | null
        match_strategy?: string | null
        submit_eligible: boolean
        already_in_diagnocat: boolean
        diagnocat_analysis_ready?: boolean
        diagnocat_analyses_count?: number
        patient_linked: boolean
      }>
      total: number
    }
  },

  async linkDiagnocatPatient(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    params: { lkPatientId: number; diagnocatUid: string; localPatientId?: number; misPatientId?: string },
  ) {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/diagnocat/link`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lk_patient_id: params.lkPatientId,
        diagnocat_uid: params.diagnocatUid,
        local_patient_id: params.localPatientId ?? params.lkPatientId,
        ...(params.misPatientId ? { mis_patient_id: params.misPatientId } : {}),
      }),
    })
    return payload as { success: boolean }
  },

  async getDiagnocatCatalogStudies(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    misPatientId: string,
    localPatientId: number,
  ) {
    const payload = await authorizedRequest(
      `${bootstrap.apiBase}/staff/diagnocat/catalog-studies?mis_patient_id=${encodeURIComponent(misPatientId)}&local_patient_id=${localPatientId}`,
      session,
    )
    return payload as {
      success: boolean
      studies: Array<{ study_uid: string; study_date: string | null; submit_eligible: boolean; already_in_diagnocat: boolean }>
    }
  },

  async submitDiagnocatCt(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    params: { misPatientId: string; localPatientId: number; studyUid: string; analysisType?: string },
  ) {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/diagnocat/submit-ct`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mis_patient_id: params.misPatientId,
        local_patient_id: params.localPatientId,
        study_uid: params.studyUid,
        analysis_type: params.analysisType ?? 'GP',
      }),
    })
    return payload as {
      success: boolean
      job: { id: string; status: string; analysis_ready?: boolean; diagnoses_count?: number; error_text?: string | null }
    }
  },

  async getDiagnocatUploadJob(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    misPatientId: string,
    jobId: string,
  ) {
    const payload = await authorizedRequest(
      `${bootstrap.apiBase}/staff/diagnocat/jobs/${encodeURIComponent(jobId)}?mis_patient_id=${encodeURIComponent(misPatientId)}`,
      session,
    )
    return payload as {
      success: boolean
      job: { id: string; status: string; analysis_ready?: boolean; diagnoses_count?: number; error_text?: string | null }
    }
  },

  async syncDiagnocatPatient(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    misPatientId: string,
    localPatientId: number,
  ) {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/diagnocat/sync-patient`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mis_patient_id: misPatientId, local_patient_id: localPatientId }),
    })
    return payload as { success: boolean; stats?: Record<string, unknown> }
  },

  async getPatientDiagnocatReportPdf(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    patientId: number,
    analysisUid: string,
  ) {
    const payload = await authorizedRequest(
      `${bootstrap.apiBase}/staff/patients/${patientId}/diagnocat/reports/${encodeURIComponent(analysisUid)}/pdf`,
      session,
    )
    return staffPatientDiagnocatPdfResponseSchema.parse(payload)
  },

  async getPatientWorkspace(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    patientId: number,
  ): Promise<StaffPatientWorkspaceData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/patients/${patientId}/workspace`, session)
    const parsed = staffPatientWorkspaceResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      patient: parsed.patient,
      summary: parsed.summary,
      appointments: parsed.appointments,
      financial_records: parsed.financial_records,
      treatment_history: parsed.treatment_history,
      actions: parsed.actions,
    }
  },

  async getOperationsFeed(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffOperationsData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/operations`, session)
    const parsed = staffOperationsResponseSchema.parse(payload)

    return {
      sources: parsed.sources,
      panels: parsed.panels,
    }
  },

  async getUsers(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffUsersData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/users`, session)
    const parsed = staffUsersResponseSchema.parse(payload)

    return {
      summary: parsed.summary,
      users: parsed.users,
      permission_matrix: parsed.permission_matrix,
    }
  },

  async getUsersCatalog(bootstrap: TenantBootstrapModel, session: AuthSession) {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/users/catalog`, session)
    const parsed = staffUsersCatalogResponseSchema.parse(payload)
    return { permissions: parsed.permissions, roles: parsed.roles }
  },

  async createUser(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    input: {
      phone: string
      full_name: string
      display_name?: string | null
      email?: string | null
      role_key: string
      branch_ids: number[]
      permissions: string[]
      linked_doctor_id?: number | null
      is_active?: boolean
    },
  ) {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/users`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    const parsed = staffUsersMutationResponseSchema.parse(payload)
    return parsed.user
  },

  async updateUser(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    userId: number,
    patch: Partial<{
      phone: string
      full_name: string | null
      display_name: string | null
      email: string | null
      role_key: string
      branch_ids: number[]
      permissions: string[]
      linked_doctor_id: number | null
      is_active: boolean
    }>,
  ) {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/users/${userId}`, session, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    const parsed = staffUsersMutationResponseSchema.parse(payload)
    return parsed.user
  },

  async setUserActive(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    userId: number,
    isActive: boolean,
  ) {
    const verb = isActive ? 'activate' : 'deactivate'
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/users/${userId}/${verb}`, session, {
      method: 'POST',
    })
    const parsed = staffUsersMutationResponseSchema.parse(payload)
    return parsed.user
  },

  async createSecretLoginLink(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    input: { targetRole: 'patient' | 'staff'; targetUserId: number; ttlMinutes?: number },
  ): Promise<{ url: string; token: string; expiresAt: string; targetRole: 'patient' | 'staff'; targetUserId: number; targetDisplayName?: string }> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/auth/secret-login-link`, session, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        target_role: input.targetRole,
        target_user_id: input.targetUserId,
        ttl_minutes: input.ttlMinutes ?? 15,
      }),
    })

    if (typeof payload !== 'object' || payload === null) {
      throw new Error('Некорректный ответ API secret-login.')
    }

    const link = (payload as { link?: Record<string, unknown> }).link
    const url = typeof link?.url === 'string' ? link.url : ''
    const token = typeof link?.token === 'string' ? link.token : ''
    const expiresAt = typeof link?.expires_at === 'string' ? link.expires_at : ''
    const targetRole = link?.target_role === 'staff' ? 'staff' : 'patient'
    const targetUserId = typeof link?.target_user_id === 'number' ? link.target_user_id : input.targetUserId
    const targetDisplayName = typeof link?.target_display_name === 'string' ? link.target_display_name : undefined

    if (!url || !token || !expiresAt) {
      throw new Error('Не удалось сформировать secret-ссылку.')
    }

    return { url, token, expiresAt, targetRole, targetUserId, targetDisplayName }
  },

  async getGiftCertificates(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffGiftCertificatesData> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/gift-certificates`, session)
    const parsed = staffGiftCertificatesResponseSchema.parse(payload)

    return {
      summary: parsed.summary,
      templates: parsed.templates,
      issued: parsed.issued,
    }
  },

  async previewGiftCertificate(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    input: StaffGiftCertificateFormInput,
  ): Promise<StaffGiftCertificate> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/gift-certificates/preview`, session, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    })

    return staffGiftCertificateMutationResponseSchema.parse(payload).data
  },

  async issueGiftCertificate(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    input: StaffGiftCertificateFormInput,
  ): Promise<StaffGiftCertificate> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/gift-certificates/issue`, session, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    })

    return staffGiftCertificateMutationResponseSchema.parse(payload).data
  },

  async getPublicGiftCertificate(
    bootstrap: TenantBootstrapModel,
    token: string,
  ): Promise<PublicGiftCertificateData> {
    const response = await fetch(`${bootstrap.apiBase}/public/gift-certificates/${encodeURIComponent(token)}`, {
      headers: {
        Accept: 'application/json',
      },
    })
    const payload = await parseJson(response)

    if (!response.ok) {
      const failure = typeof payload === 'object' && payload !== null
        ? {
            status: String((payload as Record<string, unknown>).status ?? 'not_found'),
            error: String((payload as Record<string, unknown>).error ?? 'Подарочный сертификат недоступен.'),
          }
        : {
            status: 'not_found',
            error: 'Подарочный сертификат недоступен.',
          }

      const error = new Error(failure.error) as Error & { details?: PublicGiftCertificateFailure }
      error.details = failure
      throw error
    }

    return publicGiftCertificateSchema.parse(payload).data
  },

  async getPlanDetail(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number): Promise<PublicTreatmentPlan> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/plans/${planId}`, session)

    return staffTreatmentPlanResponseSchema.parse(payload).data
  },

  async updateShare(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    planId: number,
    input: ShareSettingsInput,
  ): Promise<PublicTreatmentPlan> {
    const payload = await authorizedRequest(`${bootstrap.apiBase}/staff/plans/${planId}/share`, session, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    })

    return staffTreatmentPlanResponseSchema.parse(payload).data
  },

  async getPublicPlan(bootstrap: TenantBootstrapModel, token: string): Promise<PublicTreatmentPlan> {
    const fetchPlan = async (url: string) => {
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      })
      return { response, json: await parseJson(response) }
    }

    let { response, json } = await fetchPlan(publicConstructorPlanUrl(bootstrap, token))
    if (!response.ok) {
      const parsed = publicTreatmentPlanErrorSchema.safeParse(json)
      const shouldTryLegacy = response.status === 404 || parsed.success && parsed.data.status === 'not_found'
      if (shouldTryLegacy) {
        ;({ response, json } = await fetchPlan(publicPlanUrl(bootstrap, token)))
      }
    }

    if (!response.ok) {
      const parsed = publicTreatmentPlanErrorSchema.safeParse(json)
      const fallback: PublicPlanFailure = parsed.success
        ? parsed.data
        : {
            status: 'not_found',
            error: 'Публичный план лечения недоступен.',
          }

      const error = new Error(fallback.error) as Error & { details?: PublicPlanFailure }
      error.details = fallback
      throw error
    }

    return publicTreatmentPlanResponseSchema.parse(json).data
  },

  async recordPublicEvent(
    bootstrap: TenantBootstrapModel,
    token: string,
    event: string,
    payload: Record<string, unknown> = {},
  ) {
    const body = JSON.stringify({
      event,
      ...payload,
    })
    let response = await fetch(publicConstructorPlanEventUrl(bootstrap, token), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    })
    if (response.status === 404) {
      response = await fetch(publicPlanEventUrl(bootstrap, token), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body,
        keepalive: true,
      })
    }

    const json = await parseJson(response)
    const parsed = publicTreatmentPlanEventResponseSchema.safeParse(json)

    return {
      ok: response.ok,
      data: parsed.success ? parsed.data : null,
    }
  },

  publicPlanEventUrl,

  // Constructor plans CRUD
  async getConstructorPlans(bootstrap: TenantBootstrapModel, session: AuthSession, opts: { patientId?: number } = {}) {
    const q = opts.patientId ? `?patient_id=${opts.patientId}` : ''
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor${q}`, session)
  },

  async getConstructorPlan(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}`, session)
  },

  async createConstructorPlan(bootstrap: TenantBootstrapModel, session: AuthSession, data: { patient_id: number; patient_name?: string; title: string; description?: string }) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  },

  async updateConstructorPlan(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, fields: Record<string, unknown>) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}`, session, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    })
  },

  async activateConstructorShare(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    planId: number,
    opts: { expiresAt?: string; hidePrices?: boolean; payment?: unknown; accessPassword?: string | null } = {},
  ) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/share/activate`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        expires_at: opts.expiresAt,
        hide_prices: opts.hidePrices,
        payment: opts.payment ?? undefined,
        access_password: opts.accessPassword ?? undefined,
      }),
    })
  },

  async deactivateConstructorShare(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/share/deactivate`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
  },

  async addStage(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, data: { title: string; sort_order?: number }) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  },

  async updateStage(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, stageId: number, fields: Record<string, unknown>) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages/${stageId}`, session, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    })
  },

  async deleteStage(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, stageId: number) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages/${stageId}`, session, { method: 'DELETE' })
  },

  async reorderStages(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, stageIds: number[]) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages/reorder`, session, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage_ids: stageIds }),
    })
  },

  async addService(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, stageId: number, data: Record<string, unknown>) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages/${stageId}/services`, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  },

  async updateService(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, stageId: number, serviceId: number, fields: Record<string, unknown>) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages/${stageId}/services/${serviceId}`, session, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    })
  },

  async deleteService(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, stageId: number, serviceId: number) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages/${stageId}/services/${serviceId}`, session, { method: 'DELETE' })
  },

  async reorderServices(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, stageId: number, serviceIds: number[]) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/stages/${stageId}/services/reorder`, session, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ service_ids: serviceIds }),
    })
  },

  async uploadMedia(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, file: File) {
    const form = new FormData()
    form.append('file', file)

    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/media`, session, {
      method: 'POST',
      body: form,
    })
  },

  async deleteMedia(bootstrap: TenantBootstrapModel, session: AuthSession, planId: number, mediaId: number) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/constructor/${planId}/media/${mediaId}`, session, { method: 'DELETE' })
  },

  async setMediaShare(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    planId: number,
    mediaId: number,
    shared: boolean,
  ) {
    return authorizedRequest(
      `${bootstrap.apiBase}/staff/constructor/${planId}/media/${mediaId}/share`,
      session,
      { method: 'PATCH', body: JSON.stringify({ shared }), headers: { 'Content-Type': 'application/json' } },
    )
  },

  // ── Promotions management ────────────────────────────────────────────────

  async getPromotions(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffPromotionsListResponse> {
    const raw = await authorizedRequest(`${bootstrap.apiBase}/staff/promotions`, session)
    return staffPromotionsListResponseSchema.parse(raw)
  },

  async getPromotionSegments(bootstrap: TenantBootstrapModel, session: AuthSession): Promise<StaffSegmentsResponse> {
    const raw = await authorizedRequest(`${bootstrap.apiBase}/staff/promotions/segments`, session)
    return staffSegmentsResponseSchema.parse(raw)
  },

  async createPromotion(bootstrap: TenantBootstrapModel, session: AuthSession, input: StaffPromotionInput) {
    const raw = await authorizedRequest(`${bootstrap.apiBase}/staff/promotions`, session, {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
    })
    return staffPromotionMutationResponseSchema.parse(raw)
  },

  async updatePromotion(bootstrap: TenantBootstrapModel, session: AuthSession, id: number, input: StaffPromotionInput) {
    const raw = await authorizedRequest(`${bootstrap.apiBase}/staff/promotions/${id}`, session, {
      method: 'PUT',
      body: JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
    })
    return staffPromotionMutationResponseSchema.parse(raw)
  },

  async deletePromotion(bootstrap: TenantBootstrapModel, session: AuthSession, id: number) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/promotions/${id}`, session, { method: 'DELETE' })
  },

  async activateMisShare(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    planId: number,
    input: MisShareInput,
  ): Promise<MisShareData> {
    const raw = await authorizedRequest(
      `${bootstrap.apiBase}/staff/plans/${planId}/share/activate`,
      session,
      {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      },
    )
    const data = (raw as Record<string, unknown>)?.data as Record<string, unknown> | undefined
    return {
      public_token: (data?.public_token as string | null) ?? null,
      public_url: (data?.public_url as string | null) ?? null,
      is_active: Boolean(data?.is_active ?? false),
      expires_at: (data?.expires_at as string | null) ?? null,
      hide_prices: Boolean(data?.hide_prices ?? false),
      offer_deadline: (data?.offer_deadline as string | null) ?? null,
      note: (data?.note as string) ?? '',
      discount_percent: typeof data?.discount_percent === 'number' ? data.discount_percent : null,
      custom_title: (data?.custom_title as string | null) ?? null,
      payment: (data?.payment as import('../types/plans').PaymentSettingsInput | null) ?? null,
      has_password: Boolean(data?.has_password ?? false),
      view_count: typeof data?.view_count === 'number' ? data.view_count : 0,
      last_viewed_at: (data?.last_viewed_at as string | null) ?? null,
      last_scroll_depth_percent: typeof data?.last_scroll_depth_percent === 'number' ? data.last_scroll_depth_percent : null,
      last_read_time_seconds: typeof data?.last_read_time_seconds === 'number' ? data.last_read_time_seconds : null,
      last_click_target: (data?.last_click_target as string | null) ?? null,
    }
  },

  async deactivateMisShare(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    planId: number,
  ): Promise<void> {
    await authorizedRequest(
      `${bootstrap.apiBase}/staff/plans/${planId}/share/deactivate`,
      session,
      { method: 'POST' },
    )
  },

  async getMisShare(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    planId: number,
  ): Promise<MisShareData | null> {
    try {
      const raw = await authorizedRequest(
        `${bootstrap.apiBase}/staff/plans/${planId}/share`,
        session,
      )
      const data = (raw as Record<string, unknown>)?.data as Record<string, unknown> | undefined
      if (!data) return null
      return {
        public_token: (data.public_token as string | null) ?? null,
        public_url: (data.public_url as string | null) ?? null,
        is_active: Boolean(data.is_active ?? false),
        expires_at: (data.expires_at as string | null) ?? null,
        hide_prices: Boolean(data.hide_prices ?? false),
        offer_deadline: (data.offer_deadline as string | null) ?? null,
        note: (data.note as string) ?? '',
        discount_percent: typeof data.discount_percent === 'number' ? data.discount_percent : null,
        custom_title: (data.custom_title as string | null) ?? null,
        payment: (data.payment as import('../types/plans').PaymentSettingsInput | null) ?? null,
        has_password: Boolean(data.has_password ?? false),
        view_count: typeof data.view_count === 'number' ? data.view_count : 0,
        last_viewed_at: (data.last_viewed_at as string | null) ?? null,
        last_scroll_depth_percent: typeof data.last_scroll_depth_percent === 'number' ? data.last_scroll_depth_percent : null,
        last_read_time_seconds: typeof data.last_read_time_seconds === 'number' ? data.last_read_time_seconds : null,
        last_click_target: (data.last_click_target as string | null) ?? null,
      }
    } catch {
      return null
    }
  },

  publicMisEventUrl(bootstrap: TenantBootstrapModel, token: string) {
    return `${bootstrap.apiBase}/public/mis-plans/${encodeURIComponent(token)}/events`
  },

  async getPublicMisPlan(bootstrap: TenantBootstrapModel, token: string): Promise<Record<string, unknown>> {
    const response = await fetch(
      `${bootstrap.apiBase}/public/mis-plans/${encodeURIComponent(token)}`,
      { headers: { Accept: 'application/json' } },
    )
    const json = await parseJson(response)
    if (!response.ok) {
      const err = new Error(
        ((json as Record<string, unknown>)?.error as string) ?? 'Публичный MIS-план недоступен.',
      ) as Error & { details?: PublicPlanFailure }
      err.details = {
        status: ((json as Record<string, unknown>)?.status as string) ?? 'not_found',
        error: err.message,
      }
      throw err
    }
    return (json as Record<string, unknown>)?.data as Record<string, unknown> ?? (json as Record<string, unknown>)
  },

  recordPublicMisEvent(
    bootstrap: TenantBootstrapModel,
    token: string,
    event: string,
    payload: Record<string, unknown> = {},
  ): void {
    void fetch(`${bootstrap.apiBase}/public/mis-plans/${encodeURIComponent(token)}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, ...payload }),
      keepalive: true,
    }).catch(() => undefined)
  },

  // 03.05.26: модуль «Документы» (рассрочка / налоговая справка) — proxy в МИС.
  async listDocumentTemplates(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    documentClass: 'installment_agreement' | 'tax_certificate' | 'corp_agreement',
  ): Promise<unknown> {
    const url = `${bootstrap.apiBase}/staff/documents/templates?document_class=${encodeURIComponent(documentClass)}`
    const payload = (await authorizedRequest(url, session)) as { data?: unknown }
    return payload?.data ?? payload
  },

  async listDocuments(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    options: { patientId?: string; search?: string; documentClass?: 'installment_agreement' | 'tax_certificate' | 'corp_agreement'; limit?: number } = {},
  ): Promise<unknown> {
    const q = new URLSearchParams()
    if (options.patientId) q.set('patient_id', options.patientId)
    if (options.search) q.set('search', options.search)
    if (options.documentClass) q.set('document_class', options.documentClass)
    if (options.limit) q.set('limit', String(options.limit))
    const url = `${bootstrap.apiBase}/staff/documents/list${q.toString() ? `?${q}` : ''}`
    const payload = (await authorizedRequest(url, session)) as { data?: unknown }
    return payload?.data ?? payload
  },

  async getDocument(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    id: string,
  ): Promise<unknown> {
    const url = `${bootstrap.apiBase}/staff/documents/${encodeURIComponent(id)}`
    const payload = (await authorizedRequest(url, session)) as { data?: unknown }
    return payload?.data ?? payload
  },

  async aiProposeDocument(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    body: Record<string, unknown>,
  ): Promise<unknown> {
    const url = `${bootstrap.apiBase}/staff/documents/ai/proposal`
    const payload = (await authorizedRequest(url, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })) as { data?: unknown }
    return payload?.data ?? payload
  },

  async generateDocument(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    body: Record<string, unknown>,
  ): Promise<unknown> {
    const url = `${bootstrap.apiBase}/staff/documents/generate`
    const payload = (await authorizedRequest(url, session, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })) as { data?: unknown }
    return payload?.data ?? payload
  },

  async downloadDocument(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    id: string,
  ): Promise<{ contentType: string; contentDisposition: string; blob: Blob }> {
    const url = `${bootstrap.apiBase}/staff/documents/${encodeURIComponent(id)}/download`
    const payload = (await authorizedRequest(url, session)) as {
      data?: { content_type?: string; content_disposition?: string; content_base64?: string }
    }
    const data = payload?.data ?? {}
    const contentType = data.content_type ?? 'application/octet-stream'
    const contentDisposition = data.content_disposition ?? ''
    const base64 = data.content_base64 ?? ''
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    const blob = new Blob([bytes], { type: contentType })
    return { contentType, contentDisposition, blob }
  },

  async getPatientFinancialSummary(
    bootstrap: TenantBootstrapModel,
    session: AuthSession,
    params: { patient_id: string; period_start: string; period_end: string },
  ): Promise<{ code1_minor: number; code2_minor: number; total_minor: number; services_list: string[]; patient_name: string }> {
    const qs = new URLSearchParams(params)
    const raw = await authorizedRequest(
      `${bootstrap.apiBase}/staff/documents/patient-financial-summary?${qs.toString()}`,
      session,
    ) as { data?: Record<string, unknown> }
    const d = raw?.data ?? {}
    return {
      code1_minor: Number(d.code1_minor ?? 0),
      code2_minor: Number(d.code2_minor ?? 0),
      total_minor: Number(d.total_minor ?? 0),
      services_list: Array.isArray(d.services_list) ? (d.services_list as string[]) : [],
      patient_name: String(d.patient_name ?? ''),
    }
  },

  // ── Media repository (20.05.26) ────────────────────────────────────────

  async mediaGetFolders(bootstrap: TenantBootstrapModel, session: AuthSession) {
    const raw = await authorizedRequest(`${bootstrap.apiBase}/staff/media/folders`, session)
    const data = raw as { success?: boolean; folders?: unknown[] }
    return (data.folders ?? []) as import('../components/sections/StaffMediaSection').MediaFolder[]
  },

  async mediaList(bootstrap: TenantBootstrapModel, session: AuthSession, params: Record<string, unknown> = {}) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    }
    const raw = await authorizedRequest(`${bootstrap.apiBase}/staff/media/list?${qs.toString()}`, session)
    const data = raw as { success?: boolean; items?: unknown[]; total?: number }
    return { items: data.items ?? [], total: data.total ?? 0 }
  },

  async mediaUpload(bootstrap: TenantBootstrapModel, session: AuthSession, file: File, folderId?: number | null) {
    const form = new FormData()
    form.append('file', file)
    if (folderId != null) form.append('folder_id', String(folderId))
    return authorizedRequest(`${bootstrap.apiBase}/staff/media/upload`, session, {
      method: 'POST',
      body: form,
    })
  },

  async mediaUpdate(bootstrap: TenantBootstrapModel, session: AuthSession, id: number, data: { alt_text?: string; description?: string; folder_id?: number | null }) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/media/update`, session, {
      method: 'POST',
      body: JSON.stringify({ id, ...data }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async mediaDelete(bootstrap: TenantBootstrapModel, session: AuthSession, ids: number[]) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/media/delete`, session, {
      method: 'POST',
      body: JSON.stringify({ ids }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async mediaMove(bootstrap: TenantBootstrapModel, session: AuthSession, ids: number[], folderId: number | null) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/media/move`, session, {
      method: 'POST',
      body: JSON.stringify({ ids, folder_id: folderId }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async mediaCreateFolder(bootstrap: TenantBootstrapModel, session: AuthSession, name: string, parentId?: number | null) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/media/folders`, session, {
      method: 'POST',
      body: JSON.stringify({ name, parent_id: parentId ?? null }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async mediaUpdateFolder(bootstrap: TenantBootstrapModel, session: AuthSession, id: number, name: string) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/media/folders/${id}`, session, {
      method: 'PUT',
      body: JSON.stringify({ name }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async mediaDeleteFolder(bootstrap: TenantBootstrapModel, session: AuthSession, id: number) {
    return authorizedRequest(`${bootstrap.apiBase}/staff/media/folders/${id}`, session, { method: 'DELETE' })
  },
}
